import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest, HttpStatusCode } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { AUTH_TOKEN_INTERCEPTOR_FILTER, TOKEN_API_ENDPOINT } from '@core/configs';
import { LocalStorageHelper } from '@core/helper/local-storage.helper';
import { AuthService } from '@core/services/apis';
import { HttpCancelService, LocalStorageService } from '@core/services/common';
import { BehaviorSubject, Observable, Subject, throwError } from 'rxjs';
import { catchError, filter, switchMap, take, takeUntil, tap } from 'rxjs/operators';

@Injectable()
export class JWTInterceptor implements HttpInterceptor {
  private refreshTokenInProgress = false;
  private refreshTokenSubject: Subject<string> = new BehaviorSubject<any>(null);

  constructor(
    private authService: AuthService,
    private cancelService: HttpCancelService,
    private localStorageService: LocalStorageService,
    @Inject(AUTH_TOKEN_INTERCEPTOR_FILTER)
    protected filterRequest: (req: HttpRequest<any>) => boolean
  ) { }

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const userSession = this.localStorageService.getUserSession();
    if (!this.filterRequest(request) && !(request.body instanceof File) && userSession && userSession.accessToken) {
      if (request.url.includes(TOKEN_API_ENDPOINT.refreshToken)) {
        return next.handle(this.injectToken(request, userSession.accessToken)).pipe(
          takeUntil(
            this.cancelService.cancelPendingRequest$.pipe(
              tap(() => {
                throw new Error('Request is canceled');
              })
            )
          )
        );
      }
      return next.handle(this.injectToken(request, userSession.accessToken)).pipe(
        takeUntil(
          this.cancelService.cancelPendingRequest$.pipe(
            tap(() => {
              throw new Error('Request is canceled');
            })
          )
        ),
        catchError((error) => {
          if (error.status === HttpStatusCode.Unauthorized) {
            if (!this.refreshTokenInProgress) {
              this.refreshTokenInProgress = true;
              this.refreshTokenSubject.next('');
              return this.authService.requestAccessToken(userSession.refreshToken).pipe(
                switchMap((res) => {
                  this.refreshTokenInProgress = false;
                  this.refreshTokenSubject.next(res.result.accessToken);
                  this.localStorageService.setUserSession(res.result);
                  return next.handle(this.injectToken(request, res.result.accessToken)).pipe(
                    takeUntil(
                      this.cancelService.cancelPendingRequest$.pipe(
                        tap(() => {
                          throw new Error('Request is canceled');
                        })
                      )
                    )
                  );
                }),
                catchError((e) => {
                  this.refreshTokenInProgress = false;
                  if (e.status === HttpStatusCode.Unauthorized) {
                    this.authService.logout(true, true);
                  }
                  return throwError(error);
                })
              );
            } else {
              return this.refreshTokenSubject.pipe(
                filter(result => result !== null),
                take(1),
                switchMap((at) => next.handle(this.injectToken(request, at)).pipe(
                  takeUntil(
                    this.cancelService.cancelPendingRequest$.pipe(
                      tap(() => {
                        throw new Error('Request is canceled');
                      })
                    )
                  )
                ))
              );
            }
          }
          return throwError(error);
        })
      );
    } else {
      return next.handle(request);
    }
  }

  injectToken(request: HttpRequest<any>, accessToken: string): HttpRequest<any> {
    const moduleId = LocalStorageHelper.getModuleIdAtPermDefaultDetail() ?? "";
    return request.clone({
      headers: request.headers.set('moduleId', moduleId?.toString()),
      setHeaders: {
        authorization: `Bearer ${accessToken}`,
      }
    });
  }
}
